import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Premium screenshot showcase for the Memory Quest homepage section. Reuses
// the site's existing .phone-frame device treatment and .carousel-button
// controls so the carousel feels native to the rest of Vmemoo — only the
// track/slide/dot styling below is new (see App.css ".quest-*" rules).
export function MemoryQuestCarousel({ slides }) {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  // Suppresses the scroll-listener's own "closest to center" detection for a
  // moment after an explicit scrollToIndex() call. Without this, clicking
  // the LAST dot races against the scroll listener: the browser clamps the
  // requested scroll position (the last slide can never be perfectly
  // centered), so the listener's closest-center heuristic picks the
  // second-to-last slide instead and overwrites the explicitly requested
  // active index. Suppressing the listener while a programmatic scroll is
  // in flight lets the explicit request always win; organic user swipes
  // never call scrollToIndex, so they are unaffected.
  const suppressUntilRef = useRef(0);

  const scrollToIndex = useCallback((index) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = (index + slides.length) % slides.length;
    const target = track.children[clamped];
    if (target) {
      const maxLeft = track.scrollWidth - track.clientWidth;
      const left = target.offsetLeft - (track.clientWidth - target.clientWidth) / 2;
      track.scrollTo({ left: Math.max(0, Math.min(left, maxLeft)), behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }
    suppressUntilRef.current = Date.now() + 500;
    setActive(clamped);
  }, [slides.length]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    let frame;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (Date.now() < suppressUntilRef.current) return;
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let closestDistance = Infinity;
        [...track.children].forEach((child, index) => {
          const distance = Math.abs(child.offsetLeft + child.clientWidth / 2 - center);
          if (distance < closestDistance) { closestDistance = distance; closest = index; }
        });
        setActive(closest);
      });
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => { track.removeEventListener("scroll", onScroll); cancelAnimationFrame(frame); };
  }, []);

  const onKeyDown = (event) => {
    if (event.key === "ArrowRight") { event.preventDefault(); scrollToIndex(active + 1); }
    if (event.key === "ArrowLeft") { event.preventDefault(); scrollToIndex(active - 1); }
  };

  return (
    <div className="quest-carousel" role="group" aria-roledescription="carousel" aria-label="Vmemoo Memory Quest screenshots" data-testid="memory-quest-carousel">
      <div className="quest-track" ref={trackRef} tabIndex={0} onKeyDown={onKeyDown} data-testid="memory-quest-carousel-track">
        {slides.map((slide, index) => (
          <figure className={`quest-slide ${index === active ? "quest-slide-active" : ""}`} key={slide.alt} data-testid={`memory-quest-slide-${index + 1}`}>
            <div className="phone-frame">
              <div className="phone-speaker" aria-hidden="true" />
              <div className="phone-screen">
                <img
                  src={slide.src}
                  srcSet={`${slide.src480} 480w, ${slide.src} 900w`}
                  sizes="(max-width: 640px) 78vw, 320px"
                  alt={slide.alt}
                  width={320}
                  height={Math.round(320 * slide.ratio)}
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding="async"
                />
              </div>
            </div>
            <figcaption className="phone-caption">{slide.caption}</figcaption>
          </figure>
        ))}
      </div>
      <div className="quest-nav">
        <div className="carousel-controls">
          <button className="carousel-button" type="button" onClick={() => scrollToIndex(active - 1)} aria-label="Previous Memory Quest screenshot" data-testid="memory-quest-prev-button">
            <ArrowDownRight size={18} className="rotate-left" aria-hidden="true" />
          </button>
          <button className="carousel-button" type="button" onClick={() => scrollToIndex(active + 1)} aria-label="Next Memory Quest screenshot" data-testid="memory-quest-next-button">
            <ArrowUpRight size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="quest-dots" aria-label="Memory Quest screenshot pagination">
          {slides.map((slide, index) => (
            <button
              key={slide.alt}
              type="button"
              aria-current={index === active ? "true" : undefined}
              aria-label={`Show screenshot ${index + 1} of ${slides.length}`}
              className={`quest-dot ${index === active ? "quest-dot-active" : ""}`}
              onClick={() => scrollToIndex(index)}
              data-testid={`memory-quest-dot-${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
